/**
 * A ZIP file, built in the browser.
 *
 * Store method only — no compression. Every file that goes in here is a JPEG,
 * which is already compressed; deflating it again would spend CPU on a phone
 * to save a fraction of a percent. Store is also simple enough to be read in
 * one sitting, which matters more than the bytes.
 *
 * Deliberately not ZIP64: that caps an archive at 4 GB and any single file at
 * the same. A car's photos are a few megabytes.
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array<ArrayBuffer>): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]!) & 0xFF]! ^ (c >>> 8);
  }
  return (c ^ 0xFFFFFFFF) >>> 0;
}

/** MS-DOS date and time, which is what a ZIP entry carries. */
function dosStamp(d: Date): { time: number; date: number } {
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2)),
    date: ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

export interface ZipEntry {
  name: string;
  /** Backed by a plain ArrayBuffer: TypeScript's newer Uint8Array is generic
      over its buffer type, and Blob will not take a SharedArrayBuffer one. */
  bytes: Uint8Array<ArrayBuffer>;
  /** Defaults to now. */
  modified?: Date;
}

/**
 * Build the archive.
 *
 * Layout is the plain one: each file's local header and bytes in order, then
 * a central directory describing them all, then the end-of-directory record
 * pointing at where that started.
 */
export function makeZip(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  const central: Uint8Array<ArrayBuffer>[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.bytes);
    const size = entry.bytes.length;
    const { time, date } = dosStamp(entry.modified ?? new Date());

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034B50, true);   // local file header
    local.setUint16(4, 20, true);           // version needed
    local.setUint16(6, 0x0800, true);       // UTF-8 names
    local.setUint16(8, 0, true);            // stored, not deflated
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);        // compressed size
    local.setUint32(22, size, true);        // uncompressed size
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);           // no extra field

    chunks.push(new Uint8Array(local.buffer), name, entry.bytes);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014B50, true);     // central directory header
    dir.setUint16(4, 20, true);             // version made by
    dir.setUint16(6, 20, true);             // version needed
    dir.setUint16(8, 0x0800, true);
    dir.setUint16(10, 0, true);
    dir.setUint16(12, time, true);
    dir.setUint16(14, date, true);
    dir.setUint32(16, crc, true);
    dir.setUint32(20, size, true);
    dir.setUint32(24, size, true);
    dir.setUint16(28, name.length, true);
    dir.setUint16(30, 0, true);             // extra
    dir.setUint16(32, 0, true);             // comment
    dir.setUint16(34, 0, true);             // disk number
    dir.setUint16(36, 0, true);             // internal attributes
    dir.setUint32(38, 0, true);             // external attributes
    dir.setUint32(42, offset, true);        // where the local header sits

    central.push(new Uint8Array(dir.buffer), name);
    offset += 30 + name.length + size;
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054B50, true);       // end of central directory
  end.setUint16(4, 0, true);
  end.setUint16(6, 0, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);          // where the directory starts
  end.setUint16(20, 0, true);               // no comment

  return new Blob([...chunks, ...central, new Uint8Array(end.buffer)],
                  { type: "application/zip" });
}
